import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSuggestions, setExtractedData, setOcrInputID } from "../store/reducers";
import toast from 'react-hot-toast';
import { getProcessingModalTranslation } from './useLanguage';

// Helper function to play notification sound
const playNotificationSound = () => {
  try {
    // Create audio context
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a simple beep sound (two-tone chime)
    const playTone = (frequency, duration, delay = 0) => {
      setTimeout(() => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      }, delay);
    };
    
    // Play two-tone chime (success sound)
    playTone(523.25, 0.2, 0); // C5
    playTone(659.25, 0.3, 150); // E5
  } catch (error) {
  }
};

// Helper function to create browser notification
const createNotification = (title, body) => {
  try {
    const notification = new Notification(title, {
      body: body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'document-processing-complete',
      requireInteraction: false,
      silent: false
    });
    
    // Auto-close notification after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);
    
    // Focus window when notification is clicked
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (error) {
  }
};

// Helper function to show browser notification
const showBrowserNotification = (title, body) => {
  // Check if browser supports notifications
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return;
  }
  
  // Request permission if not already granted
  if (Notification.permission === 'default') {
    Notification.requestPermission().then(permission => {
      if (permission === 'granted') {
        createNotification(title, body);
      }
    });
  } else if (Notification.permission === 'granted') {
    createNotification(title, body);
  }
};

export function useJobStream(jobId) {
  const dispatch = useDispatch();
  const extractedData = useSelector((state) => state.documents.extractedData);
  const isDirty = useSelector((state) => state.documents.isDirty);
  const [status, setStatus] = useState(null);
  const [thinking, setThinking] = useState("");
  const [text, setText] = useState("");
  const [usage, setUsage] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  const [currentThinkingSection, setCurrentThinkingSection] = useState("");
  const [sectionKey, setSectionKey] = useState(0); // Track section changes for fade animation
  const abortControllerRef = useRef(null);
  const lastEventTypeRef = useRef('');
  const thinkingBufferRef = useRef("");
  const lastSectionStartRef = useRef(-1); // Track the start position of the current section
  const originalTitleRef = useRef(null);
  const notificationShownRef = useRef(false);
  const titleRestoreHandlersRef = useRef({ visibility: null, focus: null, click: null });
  
  // Get language preference from cookie
  const getLanguage = () => {
    const cookies = document.cookie.split(';');
    const langCookie = cookies.find(c => c.trim().startsWith('language='));
    return langCookie ? langCookie.split('=')[1] === 'false' : true; // Default to Spanish (true)
  };
  
  // Function to restore original title
  const restoreOriginalTitle = () => {
    if (originalTitleRef.current) {
      document.title = originalTitleRef.current;
      // Clean up event listeners
      if (titleRestoreHandlersRef.current.visibility) {
        document.removeEventListener('visibilitychange', titleRestoreHandlersRef.current.visibility);
        titleRestoreHandlersRef.current.visibility = null;
      }
      if (titleRestoreHandlersRef.current.focus) {
        window.removeEventListener('focus', titleRestoreHandlersRef.current.focus);
        titleRestoreHandlersRef.current.focus = null;
      }
      if (titleRestoreHandlersRef.current.click) {
        document.removeEventListener('click', titleRestoreHandlersRef.current.click);
        titleRestoreHandlersRef.current.click = null;
      }
    }
  };

  useEffect(() => {
    if (!jobId) {
      // Reset states when jobId is cleared
      setStatus(null);
      setThinking("");
      setText("");
      setUsage(null);
      setIsThinking(false);
      setCurrentThinkingSection("");
      setSectionKey(0);
      thinkingBufferRef.current = "";
      lastSectionStartRef.current = -1;
      lastEventTypeRef.current = '';
      notificationShownRef.current = false;
      originalTitleRef.current = null;
      // Clean up any existing title restore handlers
      restoreOriginalTitle();
      return;
    }
    
    // Reset notification flag for new job
    notificationShownRef.current = false;

    // Get token from sessionStorage
    const token = sessionStorage.getItem('token');
    if (!token) {
      return;
    }

    // Use fetch-based streaming to support Authorization header
    const url = `https://${process.env.REACT_APP_DOCS_API_KEY}/api/queue/${jobId}/stream`;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const streamData = async () => {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream',
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          // Handle 401 Unauthorized gracefully - don't redirect during active stream
          if (response.status === 401) {
            // Set status to indicate error but don't redirect
            setStatus({
              id: jobId,
              status: 'error',
              message: 'Authentication expired. Please refresh the page.',
              stage: 'stream_error'
            });
            // Abort the stream gracefully
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
            return;
          } else {
          return;
          }
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = '';
        let currentId = '';

        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            
            // Handle id line
            if (line.startsWith('id: ')) {
              currentId = line.substring(4).trim();
              continue;
            }
            
            // Handle event line
            if (line.startsWith('event: ')) {
              currentEvent = line.substring(7).trim();
              continue;
            }

            // Handle data line
            if (line.startsWith('data: ')) {
              const data = line.substring(6).trim();
              
              try {
                if (currentEvent === 'status') {
                  let parsed;
                  try {
                    parsed = JSON.parse(data);
                  } catch (parseError) {
                    continue; // Skip this event if parsing fails
                  }
                  
                  // Validate parsed data structure
                  if (!parsed || typeof parsed !== 'object') {
                    continue;
                  }
                  
                  // Store job ID from status event for restore functionality
                  if (parsed.id) {
                    sessionStorage.setItem('lastJobId', parsed.id);
                  }
                  
                  // Check if document_ready has been received - if so, NEVER allow any status updates that contain result data
                  // This is critical to preserve user changes after document_ready
                  const documentReadyReceived = sessionStorage.getItem('documentReadyReceived');
                  if (documentReadyReceived === 'true') {
                    // Once document_ready is received, completely block any status updates with result data
                    // This prevents ANY stream events from overwriting user changes
                    if (parsed.result || (parsed.status === "completed" && parsed.result)) {
                      // Update status but completely remove result to prevent any downstream updates
                      setStatus({
                        ...parsed,
                        result: undefined, // Remove result completely
                        status: parsed.status === "completed" ? parsed.status : parsed.status // Keep status but no result
                      });
                    } else {
                      // Allow status updates without result data (like stage changes, etc.)
                      setStatus(parsed);
                    }
                  } else {
                    // document_ready not received yet, allow normal status updates
                    setStatus(parsed);
                  }
                  
                  // If status is completed with results, clear thinking (only if document_ready not received)
                  if (parsed.status === "completed" && parsed.result && documentReadyReceived !== 'true') {
                    setThinking("");
                    setIsThinking(false);
                    // Results are already in status, don't need to set text
                  }
                  // If status changes to processing, might start new thinking
                  if (parsed.stage === 'stream_connected' || parsed.stage === 'connecting_to_model') {
                    // New stream starting, clear previous thinking
                    setThinking("");
                    setIsThinking(false);
                  }
                } else if (currentEvent === 'document_ready') {
                  try {
                    let parsed;
                    try {
                      parsed = JSON.parse(data);
                    } catch (parseError) {
                      continue; // Skip this event if parsing fails
                    }
                    
                    // Validate parsed data structure
                    if (!parsed || typeof parsed !== 'object') {
                      continue;
                    }
                    
                    const { results, ocrInputId } = parsed || {};
                    
                    // Validate results data structure with fallbacks
                    let validResults = null;
                    if (results) {
                      if (Array.isArray(results)) {
                        // Filter out null/undefined/empty objects
                        const filteredResults = results.filter(r => r !== null && r !== undefined && typeof r === 'object' && Object.keys(r).length > 0);
                        if (filteredResults.length > 0) {
                          validResults = filteredResults;
                        }
                      } else if (typeof results === 'object' && results !== null && Object.keys(results).length > 0) {
                        // Single object, convert to array
                        validResults = [results];
                      }
                    }
                    
                    // Fallback: Check sessionStorage for data if results is invalid
                    if (!validResults || validResults.length === 0) {
                      const storedData = sessionStorage.getItem('originalExtractedData');
                      if (storedData) {
                        try {
                          const parsedStored = JSON.parse(storedData);
                          if (Array.isArray(parsedStored) && parsedStored.length > 0) {
                            validResults = parsedStored;
                          } else if (parsedStored && typeof parsedStored === 'object' && Object.keys(parsedStored).length > 0) {
                            validResults = [parsedStored];
                          }
                        } catch (e) {
                        }
                      }
                    }
                    
                    // Check if documentReadyReceived flag already exists AND data is already set
                    // Only block if BOTH conditions are true (flag exists AND data exists)
                    const documentReadyAlreadyReceived = sessionStorage.getItem('documentReadyReceived') === 'true';
                    const hasExistingData = extractedData && Array.isArray(extractedData) && extractedData.length > 0;
                    
                    // If flag exists but no data, clear the flag and proceed (stale flag from previous session)
                    // This handles the case where flag persists but data was cleared on page load
                    if (documentReadyAlreadyReceived && !hasExistingData) {
                      sessionStorage.removeItem('documentReadyReceived');
                    }
                    
                    // Only block if BOTH flag exists AND data exists (user has made changes)
                    if (documentReadyAlreadyReceived && hasExistingData) {
                      // Still update ocrInputId and status, but NOT extractedData
                      if (ocrInputId) {
                        dispatch(setOcrInputID(ocrInputId));
                      }
                      const newStatus = {
                        id: status?.id || jobId,
                        status: 'completed',
                        result: undefined, // Explicitly no result to prevent updates
                        ocrInputId: ocrInputId,
                        stage: status?.stage || 'document_ready'
                      };
                      setStatus(newStatus);
                      continue; // Skip to next iteration, don't process further
                    }
                    
                    // Auto-restore data from document_ready if:
                    // 1. Either extractedData doesn't exist OR user hasn't made changes (isDirty is false)
                    // No need to check for restored connection - data should always be set automatically
                    const shouldAutoRestore = !extractedData || !isDirty;
                    

                    
                    // Store results in Redux as extractedData FIRST (before setting the flag)
                    // Use validResults (with fallbacks) instead of raw results
                    if (validResults && Array.isArray(validResults) && validResults.length > 0) {
                      if (shouldAutoRestore) {
                        // Check if extractedData already exists and if user has made changes
                        if (!extractedData || !isDirty) {
                          // Only set if no existing data or no user changes
                          dispatch(setExtractedData(validResults));
                        } else {
                        }
                      } else {
                      }
                    } else {
                    }
                    
                    // NOW set the flag AFTER we've set the data (if we did)
                    // This flag prevents ALL future stream updates from overwriting user changes
                    sessionStorage.setItem('documentReadyReceived', 'true');
                    
                    // Store ocrInputId in Redux
                    if (ocrInputId) {
                      dispatch(setOcrInputID(ocrInputId));
                    }
                    
                    // Set status to indicate document processing is complete
                    // This will trigger the modal to close, but stream stays open for suggestions
                    const newStatus = {
                      id: status?.id || jobId,
                      status: 'completed',
                      result: validResults || results, // Use validated results
                      ocrInputId: ocrInputId || null,
                      stage: status?.stage || 'document_ready'
                    };
                    setStatus(newStatus);
                    
                    // Clear thinking when document is ready
                    setThinking("");
                    setIsThinking(false);
                    
                    // Play sound, show notification, and change tab title
                    if (!notificationShownRef.current) {
                      notificationShownRef.current = true;
                      const isSpanish = getLanguage();
                      
                      // Play sound notification
                      playNotificationSound();
                      
                      // Show browser notification
                      showBrowserNotification(
                        getProcessingModalTranslation('documentsProcessed', isSpanish),
                        getProcessingModalTranslation('processingDoneNotification', isSpanish)
                      );
                      
                      // Change tab title
                      if (!originalTitleRef.current) {
                        originalTitleRef.current = document.title;
                      }
                      const doneTitle = getProcessingModalTranslation('processingDone', isSpanish);
                      document.title = `✓ ${doneTitle} - ${originalTitleRef.current}`;
                      
                      // Clean up any existing handlers first
                      restoreOriginalTitle();
                      
                      // Restore original title when user comes back to the tab (visibility change)
                      const handleVisibilityChange = () => {
                        if (!document.hidden) {
                          restoreOriginalTitle();
                        }
                      };
                      document.addEventListener('visibilitychange', handleVisibilityChange);
                      titleRestoreHandlersRef.current.visibility = handleVisibilityChange;
                      
                      // Also restore title on window focus
                      const handleFocus = () => {
                        restoreOriginalTitle();
                      };
                      window.addEventListener('focus', handleFocus);
                      titleRestoreHandlersRef.current.focus = handleFocus;
                      
                      // Also restore title on any click (user interaction)
                      const handleClick = () => {
                        restoreOriginalTitle();
                      };
                      document.addEventListener('click', handleClick, { once: true });
                      titleRestoreHandlersRef.current.click = handleClick;
                    }
                  } catch (e) {
                  }
                } else if (currentEvent === 'thinking_delta') {
                  // When thinking starts, clear previous thinking if we were in text mode
                  if (lastEventTypeRef.current === 'text_delta' || lastEventTypeRef.current === '') {
                    setThinking("");
                    setCurrentThinkingSection("");
                    thinkingBufferRef.current = "";
                    lastSectionStartRef.current = -1;
                  }
                  setIsThinking(true);
                  // Parse delta JSON format: {"delta":"text"}
                  try {
                    let deltaData;
                    try {
                      deltaData = JSON.parse(data);
                    } catch (parseError) {
                      // If not JSON, use data as-is
                      deltaData = { delta: data };
                    }
                    const delta = deltaData.delta !== undefined ? deltaData.delta : data;
                    
                    // Add to buffer
                    thinkingBufferRef.current += delta;
                    
                    // Detect section boundaries - extract only the most recent section
                    const buffer = thinkingBufferRef.current;
                    
                    // Look for section markers with more specific patterns
                    // Match: **Document 1, **Document 2, **Invoice EXP2500550, **Shipping, **Product, etc.
                    const sectionPatterns = [
                      /\*\*Document \d+/g,  // **Document 1, **Document 2, etc.
                      /\*\*Invoice [A-Z0-9]+/g,  // **Invoice EXP2500550
                      /\*\*Shipping/g,
                      /\*\*Product/g,
                    ];
                    
                    let currentSectionStart = -1;
                    
                    // Find the most recent section marker
                    for (const pattern of sectionPatterns) {
                      const matches = [...buffer.matchAll(pattern)];
                      if (matches.length > 0) {
                        const lastMatch = matches[matches.length - 1];
                        const matchIndex = lastMatch.index || 0;
                        if (matchIndex > currentSectionStart) {
                          currentSectionStart = matchIndex;
                        }
                      }
                    }
                    
                    // Also detect sections by spacing (double newlines or significant spacing)
                    // Look for patterns like \n\n or \n\n\n which indicate new sections
                    const spacingPattern = /\n\n+/g;
                    const spacingMatches = [...buffer.matchAll(spacingPattern)];
                    if (spacingMatches.length > 0) {
                      const lastSpacingMatch = spacingMatches[spacingMatches.length - 1];
                      const spacingIndex = (lastSpacingMatch.index || 0) + (lastSpacingMatch[0].length || 0);
                      // Only use spacing if it's more recent than the last section marker
                      if (spacingIndex > currentSectionStart) {
                        currentSectionStart = spacingIndex;
                      }
                    }
                    
                    // Check if we've moved to a new section (section start position changed)
                    const isNewSection = currentSectionStart !== lastSectionStartRef.current && 
                                       currentSectionStart >= 0 && 
                                       lastSectionStartRef.current >= 0;
                    
                    // If it's a new section, trigger fade animation
                    if (isNewSection) {
                      setSectionKey(prev => prev + 1);
                      lastSectionStartRef.current = currentSectionStart;
                    } else if (lastSectionStartRef.current === -1 && currentSectionStart >= 0) {
                      // First section detected
                      lastSectionStartRef.current = currentSectionStart;
                    } else if (lastSectionStartRef.current === -1) {
                      // No section markers yet, use 0 as start
                      lastSectionStartRef.current = 0;
                    }
                    
                    // Determine the current section content
                    let currentSection = "";
                    if (currentSectionStart >= 0) {
                      currentSection = buffer.substring(currentSectionStart).trim();
                    } else {
                      // No section markers found yet, show all content
                      currentSection = buffer;
                    }
                    
                    setCurrentThinkingSection(currentSection);
                    setThinking(currentSection);
                  } catch (e) {
                    // If not JSON, use data as-is
                    thinkingBufferRef.current += data;
                    setCurrentThinkingSection(thinkingBufferRef.current);
                    setThinking(thinkingBufferRef.current);
                  }
                  lastEventTypeRef.current = 'thinking_delta';
                } else if (currentEvent === 'text_delta') {
                  // When text starts, clear thinking and stop thinking mode
                  if (lastEventTypeRef.current === 'thinking_delta') {
                    setThinking("");
                    setCurrentThinkingSection("");
                    thinkingBufferRef.current = "";
                    lastSectionStartRef.current = -1;
                    setIsThinking(false);
                  }
                  // Parse delta JSON format: {"delta":"text"}
                  try {
                    let deltaData;
                    try {
                      deltaData = JSON.parse(data);
                    } catch (parseError) {
                      // If not JSON, use data as-is
                      deltaData = { delta: data };
                    }
                    
                    if (deltaData && typeof deltaData === 'object' && deltaData.delta !== undefined) {
                      setText((prev) => (prev || '') + deltaData.delta);
                    } else {
                      setText((prev) => (prev || '') + (data || ''));
                    }
                  } catch (e) {
                    // If not JSON, use data as-is
                    setText((prev) => (prev || '') + (data || ''));
                  }
                  lastEventTypeRef.current = 'text_delta';
                } else if (currentEvent === 'suggestions_ready') {
                  try {
                    let parsed;
                    try {
                      parsed = JSON.parse(data);
                    } catch (parseError) {
                      continue; // Skip this event if parsing fails
                    }
                    
                    // Validate parsed data structure
                    if (!parsed || typeof parsed !== 'object') {
                      continue;
                    }
                    
                    const { suggestions, suggestionJobId } = parsed || {};
                    
                    // CRITICAL: Ensure documentReadyReceived flag is set to prevent any data updates
                    // This ensures that even if suggestions_ready triggers any status updates, extractedData won't be modified
                    const documentReadyReceived = sessionStorage.getItem('documentReadyReceived');
                    if (documentReadyReceived !== 'true') {
                      sessionStorage.setItem('documentReadyReceived', 'true');
                    }
                    
                    // Validate suggestions structure with fallbacks
                    let validSuggestions = null;
                    if (suggestions) {
                      if (Array.isArray(suggestions)) {
                        // Filter out null/undefined/empty objects
                        const filteredSuggestions = suggestions.filter(s => 
                          s !== null && 
                          s !== undefined && 
                          typeof s === 'object' && 
                          (s.exportacion_index !== undefined || s.agents || s.senders || s.receivers)
                        );
                        if (filteredSuggestions.length > 0) {
                          validSuggestions = filteredSuggestions;
                        }
                      } else if (typeof suggestions === 'object' && suggestions !== null) {
                        // Single suggestion object, convert to array
                        validSuggestions = [suggestions];
                      }
                    }
                    
                    // Validate suggestions structure
                    // Each suggestion should have: exportacion_index, agents, senders, receivers
                    // Each agent/sender/receiver should have: {id, score, reason}
                    if (validSuggestions && Array.isArray(validSuggestions)) {
                    } else {
                    }
                    
                    // Store suggestions in Redux - this does NOT update extractedData
                    // Use validSuggestions if available, otherwise use empty array
                    const suggestionsToStore = validSuggestions || [];
                    
                    dispatch(setSuggestions(suggestionsToStore, suggestionJobId || null));
                    
                    // Verify suggestions were stored in Redux (check after a short delay)
                    setTimeout(() => {
                      const currentState = window.__REDUX_DEVTOOLS_EXTENSION__?.getState?.() || {};
                      const storedSuggestions = currentState.suggestions?.suggestions;
                    }, 100);
                    
                    // Update status with suggestionJobId - ensure no result data is included
                    setStatus((prev) => ({ 
                      ...prev, 
                      suggestionJobId,
                      result: undefined // Explicitly remove result to prevent any data updates
                    }));
                    
                    
                    // Stop the stream after suggestions_ready is received
                    // Clear sessionStorage items
                    sessionStorage.removeItem('activeJobId');
                    // Keep documentReadyReceived flag to continue protecting data
                    // sessionStorage.removeItem('documentReadyReceived'); // Keep this flag to protect data
                    // Dispatch event to clear activeJobId in Dashboard
                    window.dispatchEvent(new CustomEvent('clearActiveJobId'));
                    // Abort the stream
                    if (abortControllerRef.current) {
                      abortControllerRef.current.abort();
                      abortControllerRef.current = null;
                    }
                    // Break out of the stream loop
                    break;
                  } catch (e) {
                  }
                } else if (currentEvent === 'usage') {
                  if (process.env.NODE_ENV === 'development') {
                    try {
                      setUsage(JSON.parse(data));
                    } catch (e) {
                      setUsage(data);
                    }
                  }
                } else if (currentEvent === 'stream_closed') {
                  return; // Stream is closed
                } else {
                  // Default: try to parse as JSON and handle based on content
                  try {
                    let parsed;
                    try {
                      parsed = JSON.parse(data);
                    } catch (parseError) {
                      // If not JSON, treat as text
                      setText((prev) => (prev || '') + (data || ''));
                      continue;
                    }
                    
                    // Validate parsed data
                    if (!parsed || typeof parsed !== 'object') {
                      setText((prev) => (prev || '') + (data || ''));
                      continue;
                    }
                    
                    // If it looks like a status object
                    if (parsed.status !== undefined || parsed.stage !== undefined) {
                      setStatus(parsed);
                    } 
                    // If it has a delta field, treat as text_delta
                    else if (parsed.delta !== undefined) {
                      setText((prev) => (prev || '') + parsed.delta);
                    } else {
                      // Unknown format, log and treat as text 
                      setText((prev) => (prev || '') + (data || ''));
                    }
                  } catch (e) {
                    // If not JSON, treat as text
                    setText((prev) => (prev || '') + (data || ''));
                  }
                }
              } catch (error) {
                // If parsing fails, treat based on event type
                if (currentEvent === 'thinking_delta') {
                  try {
                    const deltaData = JSON.parse(data);
                    setThinking((prev) => prev + (deltaData.delta || data));
                  } catch (e) {
                    setThinking((prev) => prev + data);
                  }
                } else {
                  try {
                    const deltaData = JSON.parse(data);
                    setText((prev) => prev + (deltaData.delta || data));
                  } catch (e) {
                    setText((prev) => prev + data);
                  }
                }
              }
            } else if (line.trim() === '') {
              // Empty line indicates end of event, reset currentEvent and currentId
              currentEvent = '';
              currentId = '';
            }
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
        } else {
        }
      }
    };

    streamData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Clean up title restore handlers
      restoreOriginalTitle();
    };
  }, [jobId, dispatch]);

  return { status, thinking, text, usage, isThinking, sectionKey };
}

